import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, List, Avatar, Typography, Spin, message } from 'antd';
import {
    SendOutlined,
    UserOutlined,
    SmileOutlined,
    PaperClipOutlined,
    CheckOutlined
} from '@ant-design/icons';
import ChatServ from '../service/ChatServ';
import io from 'socket.io-client';
import dayjs from 'dayjs';

const { Text } = Typography;

const ChatWindow = ({ conversation, currentUser, isPanel, onMessageSent }) => {
    const currentUserId = currentUser?._id || currentUser?.id;
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [loading, setLoading] = useState(false);
    const socket = useRef(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (conversation) {
            fetchMessages();
            setupSocket();
        }
        return () => {
            if (socket.current) {
                socket.current.disconnect();
            }
        };
    }, [conversation]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const setupSocket = () => {
        const socketUrl = 'http://localhost:4000';
        socket.current = io(socketUrl);

        socket.current.on('connect', () => {
            socket.current.emit('joinConversation', conversation._id);
        });

        socket.current.on('receiveMessage', (newMessage) => {
            setMessages(prev => [...prev, newMessage]);
            if (onMessageSent) onMessageSent();
        });
    };

    const fetchMessages = async () => {
        setLoading(true);
        try {
            const res = await ChatServ.getMessages(conversation._id);
            setMessages(res.data || []);
            if (onMessageSent) onMessageSent();
        } catch (error) {
            console.error("Error fetching messages:", error);
            message.error("Failed to load messages");
        } finally {
            setLoading(false);
        }
    };

    const getOtherParticipant = () => {
        if (!conversation || !conversation.participants) return null;
        return conversation.participants.find(p => (p._id || p) !== currentUserId);
    };

    const handleSend = () => {
        if (!inputText.trim()) return;

        const other = getOtherParticipant();
        if (!other) return;

        const messageData = {
            conversationId: conversation._id,
            sender: currentUserId,
            receiver: other._id || other,
            text: inputText
        };

        socket.current.emit('sendMessage', messageData);
        setInputText('');
    };

    const getChatTitle = () => {
        const other = getOtherParticipant();
        if (!other || typeof other === 'string') return "Chat";
        return `${other.firstName} ${other.lastName}`;
    };

    const renderMessages = () => {
        if (loading) return <div className="pcu-text-center pcu-margin-top-50"><Spin /></div>;

        return (
            <div className="chat-messages">
                {messages.map((msg, index) => {
                    const isOut = msg.sender === currentUserId;
                    return (
                        <div key={msg._id || index} className={`msg-row ${isOut ? 'out' : 'in'}`}>
                            <div className="msg-bubble">
                                <div className="msg-text">{msg.text}</div>
                                <div className="msg-meta">
                                    <span className="msg-time">{dayjs(msg.createdAt).format('h:mm A')}</span>
                                    {isOut && (
                                        <span className={`msg-status ${msg.isRead ? 'pcu-color-info' : 'pcu-color-muted'}`}>
                                            <CheckOutlined className="pcu-font-10" />
                                            {msg.isRead && <CheckOutlined className="pcu-font-10 pcu-margin-left-n6" />}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>
        );
    };

    const content = (
        <div className="pcu-flex-column pcu-h-100 pcu-relative">
            {renderMessages()}

            <div className="chat-footer">
                <Button 
                    type="text" 
                    icon={<SmileOutlined className="pcu-font-22 pcu-color-muted" />} 
                    style={{ borderRadius: '8px' }}
                />
                <Button 
                    type="text" 
                    icon={<PaperClipOutlined className="pcu-font-20 pcu-color-muted" />} 
                    style={{ borderRadius: '8px' }}
                />
                <Input
                    placeholder="Type a message..."
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onPressEnter={handleSend}
                    bordered={false}
                    style={{ background: 'transparent' }}
                />
                <Button
                    className="send-btn"
                    icon={<SendOutlined />}
                    onClick={handleSend}
                    disabled={!inputText.trim()}
                />
            </div>
        </div>
    );

    if (isPanel) {
        return content;
    }

    // Fallback or other cases: can wrap in Drawer if ever needed again
    return content;
};

export default ChatWindow;
